import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Save,
  X,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import UserInformationCard from "../../components/forms/UserInformationCard"
import { usersApi, CreateUserData } from "../../services/usersApi"

interface User {
  first_name: string
  last_name: string
  email: string
  password: string
  confirm_password: string
}

interface FormErrors {
  [key: string]: string[]
}

export default function AddUser() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [emailValid, setEmailValid] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)

  const [formData, setFormData] = useState<User>({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  })

  const handleEmailValidationChange = (isValid: boolean, isAvailable: boolean | null) => {
    setEmailValid(isValid)
    setEmailAvailable(isAvailable)
  }

  const handleInputChange = (field: keyof User, value: string) => {
    // Trim trailing spaces for email field
    const processedValue = field === 'email' ? value.replace(/\s+$/, '') : value
    
    setFormData((prev) => ({ ...prev, [field]: processedValue }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: [] }))
    }

    // Real-time validation
    const newErrors = { ...errors }
    
    // First name validation
    if (field === 'first_name') {
      if (processedValue.length > 50) {
        newErrors.first_name = ["First name cannot exceed 50 characters"]
      } else if (newErrors.first_name) {
        delete newErrors.first_name
      }
    }
    
    // Last name validation
    if (field === 'last_name') {
      if (processedValue.length > 50) {
        newErrors.last_name = ["Last name cannot exceed 50 characters"]
      } else if (newErrors.last_name) {
        delete newErrors.last_name
      }
    }
    
    // Password validation
    if (field === 'password') {
      if (processedValue.length > 0 && processedValue.length < 8) {
        newErrors.password = ["Password must be at least 8 characters long"]
      } else if (newErrors.password) {
        delete newErrors.password
      }
    }
    
    // Confirm password validation
    if (field === 'confirm_password') {
      if (processedValue && processedValue !== formData.password) {
        newErrors.confirm_password = ["Passwords do not match"]
      } else if (newErrors.confirm_password) {
        delete newErrors.confirm_password
      }
    }
    
    setErrors(newErrors)
  }

  // Check if form can be submitted
  const canSubmit = () => {
    return formData.first_name.trim() && 
           formData.last_name.trim() && 
           formData.email.trim() && 
           formData.password && 
           formData.confirm_password && 
           formData.password === formData.confirm_password &&
           emailValid && // Email must be valid
           emailAvailable === true && // Email must be available
           !Object.keys(errors).length
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})

    try {
      // Validate form data with comprehensive validation
      const newErrors: FormErrors = {}
      
      // First name validation
      if (!formData.first_name.trim()) {
        newErrors.first_name = ["First name is required"]
      } else if (formData.first_name.length > 50) {
        newErrors.first_name = ["First name cannot exceed 50 characters"]
      }
      
      // Last name validation
      if (!formData.last_name.trim()) {
        newErrors.last_name = ["Last name is required"]
      } else if (formData.last_name.length > 50) {
        newErrors.last_name = ["Last name cannot exceed 50 characters"]
      }
      
      // Email validation
      if (!formData.email.trim()) {
        newErrors.email = ["Email is required"]
      }

      // Password validation
      if (!formData.password) {
        newErrors.password = ["Password is required"]
      } else if (formData.password.length < 8) {
        newErrors.password = ["Password must be at least 8 characters long"]
      }

      // Confirm password validation
      if (!formData.confirm_password) {
        newErrors.confirm_password = ["Please confirm your password"]
      } else if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = ["Passwords do not match"]
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setLoading(false)
        return
      }

      // Prepare data for API
      const userData: CreateUserData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        // Set all permissions to true for admin users
        is_staff: true,
        is_superuser: true,
        is_active: true,
      }

      // Send POST request to API
      const response = await usersApi.createUser(userData)

      if (!response.error) {
        // Success - navigate back
        navigate("/app/users")
      } else {
        // Handle API errors - check if response has errors object
        if ((response as any).errors) {
          // Backend returned structured errors
          setErrors((response as any).errors)
        } else {
          // Fallback to general error
          setErrors({ general: [response.message || "Failed to create user"] })
        }
      }
    } catch (error: any) {
      console.error("Error saving user:", error)
      
      // Try to parse the error message as JSON first (for backend validation errors)
      try {
        const errorData = JSON.parse(error.message)
        if (errorData.errors) {
          // Backend returned structured errors
          setErrors(errorData.errors)
          setLoading(false)
          return
        }
      } catch (parseError) {
        // If we can't parse as JSON, continue with string-based error handling
      }
      
      // Handle different types of errors
      if (error.message?.includes("email") || error.message?.includes("Email")) {
        setErrors({ email: ["This email address is already in use. Please use a different email."] })
      } else if (error.message?.includes("400")) {
        setErrors({ general: ["Bad Request (400) - Please check your input data"] })
      } else {
        setErrors({ general: ["Failed to create user. Please try again."] })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate("/app/users")
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleCancel} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Users
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Add New User</h1>
          <p className="text-slate-600">Enter the user information below</p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{errors.general[0]}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <UserInformationCard
            userData={formData}
            errors={errors}
            onDataChange={handleInputChange}
            alwaysEditable={true}
            isNewUser={true}
            onEmailValidationChange={handleEmailValidationChange}
          />
          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-slate-300 hover:border-slate-400"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !canSubmit()}
              className="bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Saving..." : "Save User"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
} 